import React, { useState, useEffect } from 'react';
import { AZURE } from '../../services/api';
import { VUKZ } from '../../theme/vukzTheme';
import { useGovernanceLang } from '../../contexts/GovernanceLangContext';

const MANAGER_AGENDA_LABELS = {
  pt: {
    kpiTitle: 'Indicadores de Performance',
    available: 'Disponíveis',
    avgLoad: 'Carga Média (ACWR)',
    adherence: 'Aderência',
    atRisk: 'Atletas em Risco',
    avgReadiness: 'Readiness Médio',
    sessionsWeek: 'Sessões Semana',
    checkinsToday: 'Check-ins Hoje',
    alertsTitle: '⚠️ Alertas de Performance',
    emptyData: 'Dados de performance serão exibidos aqui quando disponíveis.',
    emptyHint: 'Indicadores de carga, disponibilidade e risco do elenco.',
  },
  en: {
    kpiTitle: 'Performance Indicators',
    available: 'Available',
    avgLoad: 'Avg Load (ACWR)',
    adherence: 'Adherence',
    atRisk: 'Athletes at Risk',
    avgReadiness: 'Avg Readiness',
    sessionsWeek: 'Sessions/Week',
    checkinsToday: 'Check-ins Today',
    alertsTitle: '⚠️ Performance Alerts',
    emptyData: 'Performance data will be displayed here when available.',
    emptyHint: 'Load, availability and risk indicators for the squad.',
  },
};

/**
 * ManagerAgendaPage — Visão de Performance do Gestor
 * Foco: KPIs de carga, disponibilidade, aderência, risco e calendário consolidado
 * Removido: Instalações, Viagens, Compliance (admin)
 */

// ── Performance KPI Card ──
const KPICard = ({ label, value, unit, color, icon }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
    <div className="text-xs text-gray-400 mb-1">{icon}</div>
    <div className="text-2xl font-black" style={{ color: color || VUKZ.primary }}>
      {value ?? '—'}{unit && <span className="text-sm font-bold ml-0.5">{unit}</span>}
    </div>
    <div className="text-xs text-gray-500 mt-1">{label}</div>
  </div>
);

// ── Alert Row ──
const AlertRow = ({ alert }) => {
  const colors = {
    high: { bg: '#fef2f2', text: '#991b1b', dot: '#dc2626' },
    medium: { bg: '#fefce8', text: '#854d0e', dot: '#eab308' },
    low: { bg: '#f0fdf4', text: '#166534', dot: '#16a34a' },
  };
  const c = colors[alert.severity] || colors.medium;

  return (
    <div className="flex items-center gap-3 p-3 rounded-lg" style={{ backgroundColor: c.bg }}>
      <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: c.dot }} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate" style={{ color: c.text }}>{alert.message || alert.title}</p>
        {alert.athlete_name && <p className="text-xs text-gray-500">{alert.athlete_name}</p>}
      </div>
    </div>
  );
};

// ── Main Component ──
const ManagerAgendaPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { lang } = useGovernanceLang();
  const t = MANAGER_AGENDA_LABELS[lang] || MANAGER_AGENDA_LABELS.pt;

  useEffect(() => {
    const fetchPerformance = async () => {
      setLoading(true);
      try {
        const [dashRes, alertsRes] = await Promise.allSettled([
          AZURE.get('/agenda/role/manager/dashboard'),
          AZURE.get('/agenda/role/manager/alerts'),
        ]);

        const dashboard = dashRes.status === 'fulfilled' ? (dashRes.value.data?.data || dashRes.value.data || {}) : {};
        const alerts = alertsRes.status === 'fulfilled' ? (alertsRes.value.data?.data || alertsRes.value.data || []) : [];

        setData({ ...dashboard, alerts });
      } catch (err) {
        console.error('[ManagerAgenda] Error:', err);
        setData({ alerts: [] });
      } finally {
        setLoading(false);
      }
    };
    fetchPerformance();
  }, []);

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[1,2,3,4].map(i => <div key={i} className="h-24 bg-gray-100 rounded-xl animate-pulse" />)}
        </div>
        <div className="h-40 bg-gray-100 rounded-xl animate-pulse" />
      </div>
    );
  }

  const stats = data?.stats || data || {};
  const alerts = data?.alerts || [];

  return (
    <div className="space-y-6">
      {/* Performance KPIs */}
      <div>
        <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-3">
          {t.kpiTitle}
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <KPICard
            icon="👥"
            label={t.available}
            value={stats.athletes_available ?? stats.available_count ?? '—'}
            unit={stats.athletes_total ? `/${stats.athletes_total}` : ''}
            color="#16a34a"
          />
          <KPICard
            icon="⚡"
            label={t.avgLoad}
            value={stats.avg_acwr != null ? parseFloat(stats.avg_acwr).toFixed(2) : '—'}
            color={stats.avg_acwr > 1.5 ? VUKZ.alert.dot : stats.avg_acwr >= 1.3 ? '#374151' : VUKZ.primary}
          />
          <KPICard
            icon="✅"
            label={t.adherence}
            value={stats.adherence_pct != null ? parseFloat(stats.adherence_pct).toFixed(0) : '—'}
            unit="%"
            color={stats.adherence_pct >= 80 ? '#16a34a' : '#eab308'}
          />
          <KPICard
            icon="⚠️"
            label={t.atRisk}
            value={stats.athletes_at_risk ?? stats.risk_count ?? 0}
            color={stats.athletes_at_risk > 0 ? '#dc2626' : '#16a34a'}
          />
        </div>
      </div>

      {/* Secondary KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <KPICard icon="😴" label={t.avgReadiness} value={stats.avg_readiness != null ? parseFloat(stats.avg_readiness).toFixed(0) : '—'} unit="/100" color={VUKZ.primary} />
        <KPICard icon="🏋️" label={t.sessionsWeek} value={stats.sessions_this_week ?? '—'} color={VUKZ.primary} />
        <KPICard icon="📊" label={t.checkinsToday} value={stats.checkins_today ?? '—'} color={VUKZ.primary} />
      </div>

      {/* Performance Alerts */}
      {alerts.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-3">
            {t.alertsTitle}
          </h3>
          <div className="space-y-2">
            {alerts.slice(0, 8).map((alert, i) => (
              <AlertRow key={i} alert={alert} />
            ))}
          </div>
        </div>
      )}

      {/* Empty state */}
      {!stats.athletes_total && alerts.length === 0 && (
        <div className="text-center py-12 text-gray-400">
          <p className="text-sm">{t.emptyData}</p>
          <p className="text-xs mt-1">{t.emptyHint}</p>
        </div>
      )}
    </div>
  );
};

export default ManagerAgendaPage;
